import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "senf-atomic-design-system";
import { useAuthContext, useSchemaValidation } from "senf-shared";
import { Project, User } from "senf-shared/types";
import { toSentenceCase, useConvertCase } from "senf-shared/utils/convertCase";
import Button from "../../../core/button/Button";
import { Input } from "../../../core/inputs/Input";
import { useModals } from "../../../molecules/modalStack/ModalProvider";
import AuthResetPassword from "../../../templates/auth/AuthResetPassword";

const ProviderButton = ({
  providerId,
  formik,
  setSurveyLoginFormShown,
  project,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { signIn } = useAuthContext();
  const providerMap = {
    "google.com": {
      text: t("auth:authOptions.googleLogin"),
      icon: "logo/Google",
      provider: "google",
    },
    "facebook.com": {
      text: t("auth:authOptions.facebookLogin"),
      icon: "logo/Facebook",
      provider: "facebook",
    },
  };
  if (!providerMap[providerId]) return null;
  const { text, icon, provider } = providerMap[providerId] || {};
  return (
    <Button
      size="lg"
      text={text}
      leadingIcon={loading ? "loading" : icon}
      loading={!!loading}
      className="w-full"
      onClick={() => {
        setLoading(true);
        signIn({ provider, formikStore: formik, project }).then(() => {
          setLoading(false);
          setSurveyLoginFormShown(false);
          console.log(setSurveyLoginFormShown);
        });
      }}
    />
  );
};
const RegisterFields = ({ formik, setSurveyLoginFormShown }) => {
  const { t } = useTranslation();
  return (
    <>
      <Input
        name="handle"
        required
        placeholder={t("global:username")}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        value={formik?.values.handle}
        error={formik?.touched.handle && formik?.errors.handle}
      />
      <Input
        type="password"
        required
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        name="password"
        value={formik?.values.password}
        placeholder={t("auth:password")}
        error={formik?.touched.password && formik?.errors.password}
      />
      <Input
        type="password"
        required
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        name="confirmPassword"
        value={formik?.values.confirmPassword}
        placeholder={t("auth:confirmPassword")}
        error={
          formik?.touched.confirmPassword && formik?.errors.confirmPassword
        }
      />
    </>
  );
};
const LoginFields = ({ formik, providerId, setSurveyLoginFormShown }) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  return providerId === "password" ? (
    <>
      <Input
        name="password"
        type="password"
        required
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        value={formik?.values.password}
        placeholder={t("auth:password")}
        error={formik?.touched.password && formik?.errors.password}
      />
      <Button
        size="sm"
        variant="shapeless"
        className="px-0 hover:bg-transparent hover:underline active:bg-transparent"
        classNameWrapper="me-auto"
        text={toSentenceCase(t("auth:authOptions.forgotPassword"))}
        onClick={() =>
          openModal(<AuthResetPassword formikStore={formik} />, {
            id: "authResetPassword",
            title: `${t("auth:resetPassword")} `,
            description: t("auth:resetPasswordDescription"),
            // afterClose: () => setPage("authEmail"),
          })
        }
      />
    </>
  ) : (
    <span className="body-sm">{t("auth:loginToContinueDescription")}</span>
  );
};

const SurveyLogin = ({ formik, setSurveyLoginFormShown, project }) => {
  const { t } = useTranslation();
  const { userExists: checkIfUserExists } = useAuthContext();
  const [userExists, setUserExists] = useState<boolean>();
  const timeoutId = useRef<NodeJS.Timeout>();
  const [loading, setLoading] = useState(false);
  const [providerId, setProviderId] = useState<string | undefined>();
  useEffect(() => {
    if (!formik?.values.email) {
      setUserExists(undefined);
      setProviderId(undefined);
      formik?.setFieldValue("providerId", "");
      return;
    }
    setLoading(true);
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      checkIfUserExists(formik, false)
        .then(([provider]) => {
          setUserExists(!!provider);
          setProviderId(provider);
          formik?.setFieldValue("providerId", provider);
        })
        .catch(() => {
          formik?.setFieldValue("providerId", "");
          setProviderId(undefined);
          setUserExists(undefined);
        })
        .finally(() => setLoading(false));
    }, 1000);
  }, [formik?.values.email]);

  return (
    <div className="mx-auto flex max-w-sm flex-col gap-5">
      <h2 className="heading-xl">{t("auth:loginToContinueTitle")}</h2>
      <div className="flex flex-col gap-5">
        <Input
          name="email"
          required
          type="email"
          trailingIcon={loading ? "loading" : undefined}
          placeholder={t("auth:email")}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          value={formik?.values.email}
          error={formik?.touched.email && formik?.errors.email}
          success={userExists && !loading}
        />
        {userExists === undefined && providerId !== "password" && (
          <>
            <Divider>{t("global:or")}</Divider>
            <div className="flex flex-col gap-2">
              <ProviderButton
                providerId="google.com"
                formik={formik}
                setSurveyLoginFormShown={setSurveyLoginFormShown}
                project={project}
              />

              {/*    <ProviderButton
                providerId="facebook.com"
                formik={formik}
                setSurveyLoginFormShown={setSurveyLoginFormShown}
              /> */}
            </div>
          </>
        )}

        {userExists ? (
          <LoginFields
            formik={formik}
            providerId={providerId}
            setSurveyLoginFormShown={setSurveyLoginFormShown}
          />
        ) : (
          userExists === false && (
            <RegisterFields
              formik={formik}
              setSurveyLoginFormShown={setSurveyLoginFormShown}
            />
          )
        )}
      </div>
    </div>
  );
};

export default SurveyLogin;
